import React, { useState, useRef, useEffect } from 'react';
import type { ParsedXtreamChannel } from '@iptv/shared';
import '../CategorySelector.css';

interface Category {
  id: string | number;
  name: string;
}

interface Props {
  categories: Category[];
  channels: ParsedXtreamChannel[];
  onNext: (selectedCategories: Category[], filteredChannels: ParsedXtreamChannel[]) => void;
  onBack: () => void;
}

export function CategorySelector({ categories, channels, onNext, onBack }: Props) {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string | number>>(new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectAllRef.current) {
      const selectedCount = selectedCategoryIds.size;
      const totalCount = categories.length;
      selectAllRef.current.indeterminate = selectedCount > 0 && selectedCount < totalCount;
    }
  }, [selectedCategoryIds, categories]);

  const toggleCategory = (categoryId: string | number) => {
    const newSet = new Set(selectedCategoryIds);
    newSet.has(categoryId) ? newSet.delete(categoryId) : newSet.add(categoryId);
    setSelectedCategoryIds(newSet);
  };

  const toggleAllCategories = () => {
    if (selectedCategoryIds.size === categories.length) {
      setSelectedCategoryIds(new Set());
    } else {
      setSelectedCategoryIds(new Set(categories.map((c) => c.id)));
    }
  };

  const handleNext = () => {
    const selectedCategories = categories.filter((c) => selectedCategoryIds.has(c.id));
    const selectedCategoryNames = new Set(selectedCategories.map((c) => c.name));
    const filteredChannels = channels.filter((ch) => selectedCategoryNames.has(ch.category || 'Uncategorized'));
    onNext(selectedCategories, filteredChannels);
  };

  const selectedCount = selectedCategoryIds.size;
  const totalCount = categories.length;

  return (
    <div className="category-selector">
      <h2>Select Categories ({selectedCount}/{totalCount})</h2>

      <div className="category-list">
        <div className="select-all">
          <label>
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={selectedCount === totalCount && totalCount > 0}
              onChange={toggleAllCategories}
            />
            <span>Select All Categories</span>
          </label>
        </div>

        {categories.map((category) => {
          const categoryChannelCount = channels.filter(
            (ch) => ch.category === category.name
          ).length;
          const isChecked = selectedCategoryIds.has(category.id);

          return (
            <label key={category.id} className="category-item">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleCategory(category.id)}
              />
              <span className="category-name">
                {category.name}
              </span>
              <span className="category-count">({categoryChannelCount} channels)</span>
            </label>
          );
        })}
      </div>

      <div className="controls">
        <button onClick={onBack}>
          Back
        </button>
        <button onClick={handleNext} disabled={selectedCount === 0}>
          Next ({selectedCount} categories)
        </button>
      </div>
    </div>
  );
}
